import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { CarrierDocument, CarrierDocumentType } from '../carriers/types';

const key = (carrierId?: string | null) => ['carrier-documents', carrierId];

export function useCarrierDocuments(carrierId?: string | null, includeArchived = false) {
  return useQuery<{ documents: CarrierDocument[] }>({
    queryKey: [...key(carrierId), { includeArchived }],
    enabled: !!carrierId,
    queryFn: async () => {
      const { data } = await api.get(`/organization/carriers/${carrierId}/documents`, {
        params: includeArchived ? { includeArchived: 'true' } : {},
      });
      return data;
    },
  });
}

export interface UploadCarrierDocumentInput {
  file: File;
  type: CarrierDocumentType;
  notes?: string;
  expiresAt?: string; // yyyy-mm-dd
}

export function useUploadCarrierDocument(carrierId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, type, notes, expiresAt }: UploadCarrierDocumentInput) => {
      if (!carrierId) throw new Error('Missing carrier id');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      if (notes) formData.append('notes', notes);
      if (expiresAt) formData.append('expiresAt', expiresAt);

      const { data } = await api.post(`/organization/carriers/${carrierId}/documents`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(carrierId) });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export interface UpdateCarrierDocumentInput {
  documentId: string;
  type?: CarrierDocumentType;
  notes?: string | null;
  expiresAt?: string | null;
}

export function useUpdateCarrierDocument(carrierId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, ...patch }: UpdateCarrierDocumentInput) => {
      if (!carrierId) throw new Error('Missing carrier id');
      const { data } = await api.patch(
        `/organization/carriers/${carrierId}/documents/${documentId}`,
        patch
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(carrierId) });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useExtractCarrierDocument(carrierId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!carrierId) throw new Error('Missing carrier id');
      const { data } = await api.post(
        `/organization/carriers/${carrierId}/documents/${documentId}/extract`
      );
      return data as {
        applied: { billingFields: string[] };
        document: CarrierDocument;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(carrierId) });
      // The COI status column on the carriers table reads from the list query.
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDeleteCarrierDocument(carrierId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!carrierId) throw new Error('Missing carrier id');
      const { data } = await api.delete(
        `/organization/carriers/${carrierId}/documents/${documentId}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(carrierId) });
      // Archiving a COI can change the carriers table's coiOnFile/expiry.
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useRestoreCarrierDocument(carrierId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!carrierId) throw new Error('Missing carrier id');
      const { data } = await api.post(
        `/organization/carriers/${carrierId}/documents/${documentId}/restore`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key(carrierId) });
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDownloadCarrierDocument(carrierId?: string | null) {
  return useMutation({
    mutationFn: async ({ documentId, fileName }: { documentId: string; fileName: string }) => {
      if (!carrierId) throw new Error('Missing carrier id');

      const response = await api.get(
        `/organization/carriers/${carrierId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName || 'document';
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { documentId, fileName };
    },
  });
}

// Returns an object URL for the shared PdfPreviewDialog. The dialog revokes it.
export function usePreviewCarrierDocument(carrierId?: string | null) {
  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!carrierId) throw new Error('Missing carrier id');
      const response = await api.get(
        `/organization/carriers/${carrierId}/documents/${documentId}/download`,
        { params: { disposition: 'inline' }, responseType: 'blob' }
      );
      return URL.createObjectURL(
        new Blob([response.data], {
          type: response.headers['content-type'] || 'application/pdf',
        })
      );
    },
  });
}
